import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { isoParaDataHoraLocal, dataHoraParaISO } from '../utils';
import { formatarAbreEm } from '@/pages/fichas/utils';
import ModalConfirmar from './ModalConfirmar';
import type { PainelLoja } from '@/pages/fichas/types';

interface Props {
  eventoId: number;
  painel: PainelLoja;
  eventoInicio: string | null;
  aoMudou: () => void;
}

interface EstadoVisual {
  chip: string;
  rotulo: string;
  tom: 'success' | 'warning' | 'secondary' | 'danger';
  abreTexto: string | null;
}

function estadoVisual(painel: PainelLoja): EstadoVisual {
  switch (painel.estado) {
    case 'aberta':
      return { chip: '●', rotulo: 'Vendas abertas', tom: 'success', abreTexto: null };
    case 'agendada':
      return {
        chip: '🔒',
        rotulo: 'Vendas fechadas',
        tom: 'warning',
        abreTexto: painel.abre_em ? `abrem ${formatarAbreEm(painel.abre_em)}` : null,
      };
    case 'encerrada':
      return { chip: '●', rotulo: 'Vendas encerradas', tom: 'secondary', abreTexto: null };
    default:
      return { chip: '🔒', rotulo: 'Vendas fechadas', tom: 'warning', abreTexto: null };
  }
}

const TOM_CHIP: Record<EstadoVisual['tom'], string> = {
  success: 'bg-success-100 text-success-800',
  warning: 'bg-warning-100 text-warning-800',
  secondary: 'bg-secondary-100 text-foreground-700',
  danger: 'bg-danger-100 text-danger-800',
};

// Cartão mais importante: o estado das vendas e o botão de abrir/fechar.
export default function EstadoVendas({ eventoId, painel, eventoInicio, aoMudou }: Props) {
  const [confirmarFechar, setConfirmarFechar] = useState(false);
  const [trocando, setTrocando] = useState(false);
  const [agendando, setAgendando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [quando, setQuando] = useState(isoParaDataHoraLocal(eventoInicio));

  const aberta = painel.estado === 'aberta';
  const encerrada = painel.estado === 'encerrada';
  const visual = estadoVisual(painel);

  async function abrirAgora() {
    setTrocando(true);
    setErro(null);
    try {
      const { error } = await supabase.rpc('loja_abrir_vendas', { p_evento: eventoId });
      if (error) throw error;
      aoMudou();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setTrocando(false);
    }
  }

  async function fechar() {
    setTrocando(true);
    setErro(null);
    try {
      const { error } = await supabase.rpc('loja_fechar_vendas', { p_evento: eventoId });
      if (error) throw error;
      setConfirmarFechar(false);
      aoMudou();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setTrocando(false);
    }
  }

  async function agendar() {
    setAgendando(true);
    setErro(null);
    try {
      const { error } = await supabase.rpc('loja_abrir_vendas', {
        p_evento: eventoId,
        p_quando: dataHoraParaISO(quando),
      });
      if (error) throw error;
      aoMudou();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setAgendando(false);
    }
  }

  return (
    <div className="rounded-3xl bg-white p-5 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <span
        className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[15px] font-bold ${TOM_CHIP[visual.tom]}`}
      >
        <span className="text-[18px] leading-none">{visual.chip}</span>
        {visual.rotulo}
      </span>

      {visual.abreTexto && (
        <p className="mt-2 text-[14px] text-foreground-600">{visual.abreTexto}</p>
      )}

      <div className="mt-4">
        {!encerrada && (
          <button
            type="button"
            onClick={aberta ? () => setConfirmarFechar(true) : abrirAgora}
            disabled={trocando}
            className={`flex min-h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl px-6 text-[15px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              aberta
                ? 'border border-danger-300 bg-white text-danger-600 hover:bg-danger-50'
                : 'bg-primary-500 text-white shadow-lg shadow-primary-500/30 hover:bg-primary-600'
            }`}
          >
            {trocando && (
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-loader-4-line animate-spin" />
              </span>
            )}
            {aberta ? 'Fechar vendas' : 'Abrir vendas agora'}
          </button>
        )}
      </div>

      {!aberta && !encerrada && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <span className="text-[14px] text-foreground-500">abrir sozinho em</span>
          <input
            type="datetime-local"
            value={quando}
            onChange={(e) => setQuando(e.target.value)}
            className="rounded-2xl border border-background-200 bg-white px-3 py-2.5 text-[14px] text-foreground-950 outline-none focus:border-primary-500"
          />
          <button
            type="button"
            onClick={agendar}
            disabled={agendando || !quando}
            className="flex min-h-[44px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-primary-100 bg-white px-4 text-[14px] font-semibold text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {agendando && (
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-loader-4-line animate-spin" />
              </span>
            )}
            Agendar
          </button>
        </div>
      )}

      {erro && <p className="mt-2 text-[14px] text-danger-600">{erro}</p>}

      {painel.fim_em && (
        <p className="mt-3 text-[13px] text-foreground-500">
          As vendas fecham sozinhas no fim do evento ({formatarAbreEm(painel.fim_em)}).
        </p>
      )}

      {confirmarFechar && (
        <ModalConfirmar
          titulo="Fechar vendas"
          mensagem={<>Quem já comprou continua retirando. Fechar?</>}
          rotuloConfirmar="Fechar"
          confirmando={trocando}
          aoConfirmar={fechar}
          aoCancelar={() => setConfirmarFechar(false)}
        />
      )}
    </div>
  );
}